import { FC, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Check } from '@mui/icons-material'
import { Typography } from '@mui/material'

import { GRTable, GRTableColumn } from '../../../../../components/GRTable/GRTable'
import { rowActionsColumn } from '../../../../components/RowActions/RowActions'
import { FeatureChoiceValues } from '../helpers/featureHelpers'

type CustomProps = {
  disabled: boolean
  onEdit: (index: number) => void
  onRemove: (index: number) => void
}

type FeatureChoicesTableProps = CustomProps & {
  choices: FeatureChoiceValues[]
  onReorder: (choices: FeatureChoiceValues[]) => void
}

const Flag: FC<{ on: boolean; label: string }> = ({ on, label }) =>
  on ? <Check color="success" aria-label={label} /> : <Typography color="text.secondary">–</Typography>

export const FeatureChoicesTable: FC<FeatureChoicesTableProps> = ({ choices, disabled, onEdit, onRemove, onReorder }) => {
  const { t } = useTranslation()

  const tableColumns: GRTableColumn<FeatureChoiceValues, CustomProps>[] = useMemo(
    () => [
      {
        labelAccessor: '#',
        accessor: ({ rowIndex }) => rowIndex + 1,
        headerCellProps: { sx: { width: 60 } },
      },
      {
        labelAccessor: t('internal-analysts:categories_choice_name'),
        accessor: ({ row }) => <Typography fontWeight="bold">{row.labels.en}</Typography>,
        cellProps: { align: 'left' },
        headerCellProps: { align: 'left', sx: { minWidth: 240 } },
      },
      {
        labelAccessor: t('internal-analysts:categories_choice_active'),
        accessor: ({ row }) => <Flag on={row.active} label={t('internal-analysts:categories_choice_active')} />,
        headerCellProps: { sx: { width: 90 } },
      },
      {
        labelAccessor: t('internal-analysts:categories_choice_show_in_lists'),
        accessor: ({ row }) => <Flag on={row.showInLists} label={t('internal-analysts:categories_choice_show_in_lists')} />,
        headerCellProps: { sx: { width: 120 } },
      },
      rowActionsColumn<FeatureChoiceValues, CustomProps>({
        actions: ({ row, rowIndex, customTableProps }) => [
          {
            type: 'edit',
            label: t('internal-analysts:categories_choice_edit'),
            ariaLabel: t('internal-analysts:categories_choice_edit_named', { name: row.labels.en }),
            onClick: () => customTableProps?.onEdit(rowIndex),
            disabled: !!customTableProps?.disabled,
          },
          {
            type: 'delete',
            label: t('internal-analysts:categories_choice_remove'),
            ariaLabel: t('internal-analysts:categories_choice_remove_named', { name: row.labels.en }),
            onClick: () => customTableProps?.onRemove(rowIndex),
            disabled: !!customTableProps?.disabled,
          },
        ],
        width: 120,
      }),
    ],
    [t]
  )
  const [columns, setColumns] = useState(tableColumns)
  const customProps = useMemo<CustomProps>(() => ({ disabled, onEdit, onRemove }), [disabled, onEdit, onRemove])

  return (
    <GRTable
      rows={choices}
      columns={columns}
      onColumnsUpdated={setColumns}
      rowIdKey={(row) => row.key}
      customProps={customProps}
      onSortRows={onReorder}
      sortable={!disabled}
      noRowsLabel={t('internal-analysts:categories_no_choices')}
      striped
    />
  )
}
